import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import {
  BrandTemplateKind,
  DESIGN_ASPECT_PRESETS,
  ARCHETYPE_CATALOG,
  COLOR_EMPHASIS_OPTIONS,
  COLOR_MODE_OPTIONS,
  DESIGN_BACKGROUND_SOURCES,
  isGenerationReferenceMime,
  modelsFor,
  findModel,
  maxReferencesFor,
  DESIGN_MAX_EDGE,
  DESIGN_MIN_EDGE,
  DESIGN_MODE_LABEL,
  DESIGN_MODE_STATUS,
  DESIGN_TARGETS,
  TEMPLATE_MODES,
  POST_PROMPT_STYLES,
  ratioDrift,
  DESIGN_RATIO_WARN_THRESHOLD,
  type BrandWorkspaceTemplate,
  type ArchetypeId,
  type ColorEmphasis,
  type ColorMode,
  type DesignBackgroundSource,
  type MediaGeneratorKey,
  type DesignMode,
  type DesignTarget,
  type TemplateMode,
  type GeneratedDesign,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { awaitDesignJob } from './designJob';
import { designPreviewState } from './previewState';
import { MediaReferenceCard, type MediaReference } from '../media/MediaReferenceCard';
import { MediaLibraryOverlay } from '../media/MediaLibraryOverlay';
import { findCachedAsset } from '../media/mediaCache';
import { useProxyImage } from '../../lib/useProxyImage';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Field';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { MediaLibraryPanel } from '../composer/MediaLibraryPanel';
import { downloadAssetByUrl } from '../media/mediaDisplay';
import { RadioRows } from './RadioRows';
import { IMAGE_GENERATORS } from './imageGenerators';
import { useDesignPreview } from './useDesignPreview';
import { SystemTemplatePanel } from './SystemTemplatePanel';
import { PostContextBand } from '../post-context/PostContextBand';
import { PromptFromPostRow } from '../post-context/PromptFromPostRow';
import { firstTwoLines, type PostContext, type SaveToPost } from '../post-context/postContext';

/** Which of the three mutually exclusive views the right pane is showing. */
type Pane = 'preview' | 'template' | 'image';

export interface GenerateDesignSeed {
  /** Pre-filled headline, extracted from a post's TEXT block by the caller. */
  text?: string;
  /** Set when the modal was opened from a post, which changes the header and the close behaviour. */
  fromPost?: boolean;
}

/**
 * The picture styles offered for a design's image. No infographic: a design already carries its
 * text, and a diagram behind it would compete with the headline.
 */
const DESIGN_IMAGE_STYLES = ['illustration', 'photo'] as const;

/**
 * Generate Design — build one design file from the workspace brand, a headline, and optionally a
 * stored template.
 *
 * Replaces DesignBundleModal at every call site. The bundle handed over a ZIP of every artboard size;
 * this hands over the one design that was asked for, and can start from a template the workspace
 * already uses instead of a generic layout.
 */
export function GenerateDesignModal({
  open,
  onClose,
  seed,
  postContext,
  onSaveToPost,
}: {
  open: boolean;
  onClose: () => void;
  seed?: GenerateDesignSeed;
  /** The post this was opened from: shows it above the form, with text and prompts drafted from it. */
  postContext?: PostContext;
  /** Set by the post editor: "Save and return to post" puts a PNG copy of the design in the post. */
  onSaveToPost?: SaveToPost;
}) {
  const queryClient = useQueryClient();
  const [pane, setPane] = useState<Pane>('preview');
  const [target, setTarget] = useState<DesignTarget>('figma');
  const [template, setTemplate] = useState<BrandWorkspaceTemplate | null>(null);
  const [templateMode, setTemplateMode] = useState<TemplateMode>('as-is');
  /**
   * Which KIND of base the design starts from. There is no third state: v2 removed the auto path, so
   * one of these is always chosen before Generate enables.
   */
  const [templateSource, setTemplateSource] = useState<'system' | 'custom'>('system');
  const [archetype, setArchetype] = useState<ArchetypeId | null>(null);
  const [emphasis, setEmphasis] = useState<ColorEmphasis>('keep');
  const [colorMode, setColorMode] = useState<ColorMode>('keep');
  /**
   * The seed the next Generate will use, held in state so the THUMBNAIL renders the same design.
   * "Try another version" re-rolls it, which is now the whole variety mechanism.
   */
  const [variantSeed, setVariantSeed] = useState(() => Math.floor(Math.random() * 2_147_483_647));

  const [text, setText] = useState('');
  const [subtext, setSubtext] = useState('');
  const [presetId, setPresetId] = useState<string>('portrait');
  const [width, setWidth] = useState(1080);
  const [height, setHeight] = useState(1350);
  /**
   * The ratio width/height are currently locked to, or null once the user chose Custom.
   *
   * LOCAL STATE, not derived from `presetId`, because the user keeps typing into the fields after
   * picking a preset: with 4:5 locked, typing 2000 into Width must set Height to 2500, and a derived
   * value would have to re-read the preset the fields no longer match.
   */
  const [ratioLock, setRatioLock] = useState<number | null>(1080 / 1350);
  /**
   * EXPLICIT state, not derived from imageAssetId.
   *
   * The control used to read its value from whether an image was chosen, which works for two options
   * and has nowhere to put a third: "flat colours" and "generate art for me" are both "no image
   * chosen", and they must not mean the same thing — one is free and one spends a daily generation.
   */
  const [backgroundSource, setBackgroundSource] = useState<DesignBackgroundSource>('flat');
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageGenerator, setImageGenerator] = useState<string>('azure');
  const [imageModel, setImageModel] = useState<string>('');
  const [imageAssetId, setImageAssetId] = useState<string | null>(null);
  /** The chosen image's small copy, for the radio row's chip. Null when it has none. */
  const [imageThumbUrl, setImageThumbUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedDesign | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * The server's own progress sentence while a job runs, or null when nothing is running.
   *
   * A sentence rather than a percentage, because there is no honest percentage to show: an image model
   * reports nothing until it is done. What the server can say truthfully is what it is doing and for
   * how long, and a number that keeps moving is what separates "working" from "hung".
   */
  const [progress, setProgress] = useState<string | null>(null);
  /**
   * Reference images for a generated picture, in selection order.
   *
   * Order matters and is preserved end to end: some models treat the first reference as the primary
   * one, so re-sorting these anywhere would quietly change which image leads.
   */
  const [imageRefs, setImageRefs] = useState<MediaReference[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploadSignal, setUploadSignal] = useState(0);

  const templates = useQuery({
    queryKey: ['brand-templates'],
    queryFn: ({ signal }) => api.listBrandTemplates(undefined, signal),
    enabled: open,
  });

  /** Templates for the current target. Switching target changes which files are even applicable. */
  const wantedKind =
    target === 'figma' ? BrandTemplateKind.FIGMA_SVG : BrandTemplateKind.CANVA_PPTX;
  const available = useMemo(
    () => (templates.data ?? []).filter((t) => t.kind === wantedKind),
    [templates.data, wantedKind],
  );

  // Seed the headline whenever the dialog opens from a post. Keyed on the seed text so reopening
  // with different post copy re-fills, but typing in the field is never overwritten mid-edit.
  useEffect(() => {
    if (!open) return;
    setText(seed?.text ?? '');
    setError(null);
    setResult(null);
    setPane('preview');
  }, [open, seed?.text]);

  /**
   * The thumbnail, and the same design Generate will return.
   *
   * Only for system archetypes: a custom template is a file, so its preview is the file itself, which
   * the chip below already renders through the proxy.
   */
  const preview = useDesignPreview({
    archetype: templateSource === 'system' ? archetype : null,
    width,
    height,
    emphasis,
    mode: colorMode,
    headline: text,
    subhead: subtext,
    hasImage: backgroundSource !== 'flat',
    variantSeed,
  });

  const mode: DesignMode = !template
    ? 'freeform'
    : templateMode === 'redesign'
      ? 'template-redesigned'
      : templateMode === 'restyle'
        ? 'template-restyled'
        : 'template-as-is';

  /**
   * Restyle is the one mode a .pptx still cannot do.
   *
   * Use as-is now fills a PowerPoint template's mapped regions in place, leaving every other part of
   * the archive untouched. Restyle rewrites every COLOUR in the file, which for OOXML means editing
   * theme, master and shape properties together — a different job. Offering an option the server
   * will refuse is worse than not offering it, so it is disabled here with the reason.
   */
  const pptxNoRestyle = template?.kind === BrandTemplateKind.CANVA_PPTX;

  /**
   * Whether a picture can go anywhere in this design.
   *
   * A static-background template is excluded on purpose: its background IS the image, so there is
   * nowhere to put a second one — the generator would accept the pick and discard it, which is the
   * failure mode this whole field is arranged to avoid.
   */
  const templateTakesImage =
    template == null || (!template.staticOnly && Boolean(template.roleMap?.image));

  /** The soft warning, before generating rather than after. */
  const ratioWarning = useMemo(() => {
    if (!template?.widthPx || !template.heightPx || !(width > 0) || !(height > 0)) return null;
    const native = template.widthPx / template.heightPx;
    if (ratioDrift(native, width / height) <= DESIGN_RATIO_WARN_THRESHOLD) return null;
    return `This template was built for ${template.widthPx}×${template.heightPx} — content may need repositioning after import.`;
  }, [template, width, height]);

  const generate = useMutation({
    mutationFn: async () => {
      setProgress(null);
      const response = await api.generateDesign({
        target,
        templateId: templateSource === 'custom' ? (template?.id ?? null) : null,
        archetype: templateSource === 'system' ? (archetype ?? undefined) : undefined,
        colorEmphasis: emphasis,
        colorMode,
        templateMode: template ? templateMode : undefined,
        text: text.trim(),
        subtext: subtext.trim() || undefined,
        width,
        height,
        imageAssetId: templateTakesImage && backgroundSource === 'library' ? imageAssetId : null,
        backgroundSource: templateTakesImage ? backgroundSource : 'flat',
        imagePrompt: generatesImagery ? imagePrompt.trim() || undefined : undefined,
        imageGenerator: generatesImagery ? imageGenerator : undefined,
        imageModel: generatesImagery ? imageModel || undefined : undefined,
        // Only for a picture. Abstract art has no subject to be consistent with, which is why that
        // option shows no reference block.
        imageReferenceIds:
          backgroundSource === 'image'
            ? imageRefs.filter((r) => r.assetId).map((r) => r.assetId!)
            : undefined,
      });
      // Flat and library designs come straight back — there is nothing slow about them. Only
      // generating imagery is worth a job, and only then is there progress worth showing.
      if (response.status === 'ready') return response.design;
      setProgress('Starting…');
      return awaitDesignJob(response.jobId, setProgress);
    },
    onSuccess: (design) => {
      setResult(design);
      setProgress(null);
      setError(null);
      setPane('preview');
      // The design is already saved, so the library must show it — the spec's "appears at the top of
      // the grid immediately" is this invalidation, not a second write.
      void queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (err) => {
      setProgress(null);
      // A job failure is a plain Error carrying the server's own sentence, so it is shown as-is —
      // the reason ("Replicate reported: NSFW content detected") is the actionable half.
      setError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : 'Could not generate that design.',
      );
    },
  });

  /** "Download and close": the editable file to this computer, then back with nothing added. */
  const download = useMutation({
    mutationFn: (design: GeneratedDesign) => downloadAssetByUrl(design.fileUrl, design.filename),
    onSuccess: () => onClose(),
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not download the design.'),
  });

  /**
   * "Save and return to post": a PNG copy of the design, saved to the library and put in the post.
   *
   * A post takes JPG, PNG or MP4, never the editable file. When the copy was drawn with a stand-in
   * font the post says so, for someone who closed the window without reading the warning here.
   */
  const savePng = useMutation({
    mutationFn: (assetId: string) => api.designPngCopy(assetId),
    onSuccess: (copy) => {
      onSaveToPost?.(
        {
          id: copy.assetId,
          blobUrl: copy.fileUrl,
          mimeType: copy.mimeType,
          posterUrl: null,
          thumbUrl: copy.thumbUrl,
        },
        copy.missingFonts.length > 0
          ? `Added a PNG copy. It uses a similar font in place of ${familyList(copy.missingFonts)}.`
          : undefined,
      );
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      onClose();
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : 'Could not add the design to your post.',
      ),
  });

  /** Either way out is in flight: the window holds still until it lands. */
  const leaving = download.isPending || savePng.isPending;

  /**
   * How many references the CHOSEN model takes — asked of the shared registry, not assumed.
   *
   * Zero is a real answer, and the card says which model refuses rather than hiding: a reference block
   * that vanishes when you switch model reads as a bug, while one that explains itself reads as a fact
   * about that model.
   *
   * No model chosen means the generator's default, the one the server runs, so it is looked up the
   * way the server looks it up. Matching '' against the registry found nothing, and the card told a
   * FLUX user their model takes no references when it takes five.
   */
  const referenceModel = findModel(imageGenerator as MediaGeneratorKey, imageModel || undefined);
  const maxImageRefs = referenceModel ? maxReferencesFor(referenceModel.caps) : 0;

  /** Apply a preset: fill both fields and lock the ratio. Custom clears the lock. */
  const applyPreset = (id: string) => {
    setPresetId(id);
    if (id === 'custom') {
      setRatioLock(null);
      return;
    }
    const preset = DESIGN_ASPECT_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    setWidth(preset.width);
    setHeight(preset.height);
    setRatioLock(preset.width / preset.height);
  };

  /** Editing one edge scales the other while a ratio is locked. */
  const editEdge = (edge: 'width' | 'height', raw: string) => {
    const value = Number(raw);
    const next = Number.isFinite(value) ? Math.round(value) : 0;
    if (edge === 'width') {
      setWidth(next);
      if (ratioLock && next > 0) setHeight(Math.max(1, Math.round(next / ratioLock)));
    } else {
      setHeight(next);
      if (ratioLock && next > 0) setWidth(Math.max(1, Math.round(next * ratioLock)));
    }
  };

  const chooseTemplate = (picked: BrandWorkspaceTemplate | null) => {
    setTemplate(picked);
    // 'as-is' for both kinds now: filling a .pptx in place is supported, and it is what picking
    // your own template means. An unmapped one is told to map itself rather than quietly
    // re-composing into something that is not the template the person chose.
    setTemplateMode('as-is');
    setPane('preview');
    if (!picked?.widthPx || !picked.heightPx) return;

    /**
     * Adopt the template's NATIVE size, exactly.
     *
     * v1 snapped to the nearest preset, and on the design team's real files that meant a 940×788 Canva
     * export and a 1200×983 Figma one both became 1080×1080 squares — reshaping compositions nobody
     * asked to reshape. A template's own dimensions are the only sensible starting point; a preset is
     * used only when it matches exactly, and anything else becomes Custom with the real numbers in the
     * fields. Editable afterwards — a starting point, not a lock.
     */
    const exact = DESIGN_ASPECT_PRESETS.find(
      (p) => p.width === picked.widthPx && p.height === picked.heightPx,
    );
    setWidth(picked.widthPx);
    setHeight(picked.heightPx);
    if (exact) {
      setPresetId(exact.id);
      setRatioLock(exact.width / exact.height);
    } else {
      setPresetId('custom');
      setRatioLock(null);
    }
  };

  const edgesValid =
    width >= DESIGN_MIN_EDGE &&
    width <= DESIGN_MAX_EDGE &&
    height >= DESIGN_MIN_EDGE &&
    height <= DESIGN_MAX_EDGE;
  /**
   * A base must be chosen. There is no auto path any more, so Generate stays disabled rather than
   * quietly picking a layout on the person's behalf — which is the thing v2 set out to remove.
   */
  const hasBase = templateSource === 'system' ? archetype != null : template != null;
  const generatesImagery = backgroundSource === 'abstract' || backgroundSource === 'image';
  /**
   * A photographic prompt has no sensible default: brand colours describe a design, not a
   * subject. An ABSTRACT prompt may be left empty, because its brief is derived from the palette
   * and the two colour treatments.
   */
  const needsImageDescription = backgroundSource === 'image' && !imagePrompt.trim();
  /**
   * "Media Library image" with nothing chosen is not a design instruction.
   *
   * Generating anyway produced a design with an empty picture region and no explanation — the request
   * carried a null asset id, so the server had nothing to place and said nothing about it.
   */
  const needsLibraryImage = backgroundSource === 'library' && !imageAssetId;
  const canGenerate =
    hasBase &&
    text.trim().length > 0 &&
    edgesValid &&
    !needsImageDescription &&
    !needsLibraryImage &&
    !generate.isPending;

  /**
   * Why Generate is unavailable, in the order a person fills the form in.
   *
   * A disabled button with no explanation is indistinguishable from a broken one — switching to Custom
   * template without picking one left Generate dead and silent, and it was reported as "the button
   * never activates". The fix is always one field away; saying which field is the whole job. Null when
   * the button works, and `needsImageDescription` is excluded because it already has its own message
   * beside the field it concerns.
   */
  const blockedBecause =
    !hasBase
      ? templateSource === 'custom'
        ? 'Choose a custom template to continue.'
        : 'Choose a system template to continue.'
      : needsLibraryImage
        ? 'Choose an image from your Media Library, or pick a different image option.'
        : text.trim().length === 0
          ? 'Add the line you want on the design.'
          : !edgesValid
            ? 'Enter a width and height within the allowed range.'
            : null;

  /**
   * The generator, model and prompt — the settings that belong to the two GENERATING image options.
   *
   * Rendered as those rows' conditional reveal rather than after the whole group, so it sits under the
   * option it configures. Below the group it appeared beneath "Media Library image", which is the one
   * option it has nothing to do with.
   */
  const imageOptionsPanel = (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Generator">
          <Select
            aria-label="Image generator"
            value={imageGenerator}
            onChange={(e) => {
              setImageGenerator(e.target.value);
              // Models are generator-specific, so a stale one would be sent to a
              // provider that has never heard of it.
              setImageModel('');
            }}
          >
            {IMAGE_GENERATORS.map((g) => (
              <option key={g.key} value={g.key}>
                {g.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Model">
          <Select
            aria-label="Image model"
            value={imageModel}
            onChange={(e) => setImageModel(e.target.value)}
          >
            <option value="">Default</option>
            {modelsFor(imageGenerator as MediaGeneratorKey).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* Directly above the field it fills. An abstract has no subject, so its row suggests styles
          instead of kinds of picture. Keyed on the option so switching resets what it says. */}
      {postContext && (
        <PromptFromPostRow
          key={backgroundSource}
          context={postContext}
          kind={backgroundSource === 'abstract' ? 'abstract' : 'image'}
          styles={
            backgroundSource === 'abstract' ? POST_PROMPT_STYLES.abstract : DESIGN_IMAGE_STYLES
          }
          label={
            backgroundSource === 'abstract'
              ? 'Suggest an abstract style from your post'
              : 'Generate image prompt from your post'
          }
          onDrafted={setImagePrompt}
        />
      )}

      <Field
        label={backgroundSource === 'abstract' ? 'Abstract style' : 'Describe the image'}
        hint={
          backgroundSource === 'abstract'
            ? 'Optional — left empty it follows your brand colours and the treatments above.'
            : undefined
        }
      >
        <Textarea
          rows={2}
          value={imagePrompt}
          aria-label={
            backgroundSource === 'abstract' ? 'Abstract style' : 'Image description'
          }
          placeholder={
            backgroundSource === 'abstract'
              ? 'e.g. flowing gradient mesh, grainy texture, soft geometric shapes'
              : postContext
                ? 'Describe the image you want, or generate a prompt from your post.'
                : 'Describe the image you want…'
          }
          onChange={(e) => setImagePrompt(e.target.value)}
        />
      </Field>

      {backgroundSource === 'image' && (
        // Pictures only. Reference images are how a campaign stays visually consistent —
        // pin a brand photo and every image in the series shares its look — and there is
        // nothing for an abstract texture to be consistent WITH.
        <MediaReferenceCard
          refs={imageRefs}
          max={maxImageRefs}
          onChange={setImageRefs}
          disabledReason={
            maxImageRefs === 0
              ? `${referenceModel?.label ?? 'This model'} doesn’t accept reference images.`
              : undefined
          }
          onBrowseLibrary={() => setLibraryOpen(true)}
          uploadSignal={uploadSignal}
        />
      )}

      {needsImageDescription && (
        <p className="text-xs text-red-600 dark:text-error-dark">
          Describe what the picture should show — brand colours describe a design, not a
          subject, so there is nothing to derive one from.
        </p>
      )}

      {/* No aspect-ratio or resolution control, deliberately: the image goes into a
          defined region of a chosen layout at a known canvas size, so its dimensions are
          already decided. Offering a ratio picker would let someone choose a value the
          system then crops away. */}
      <p className="text-xs text-ink-muted">
        {backgroundSource === 'abstract'
          ? 'Produces texture and form, never a subject — a representational description still returns abstract art.'
          : 'Produces a picture of what you describe, sized to this layout’s image region automatically.'}{' '}
        Uses one of this workspace’s daily image generations. The background layer isn’t
        editable in Figma or Canva; text, shapes and layout stay fully editable.
      </p>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="5xl"
      title="Generate design"
      bodyClassName="p-0"
      // Refused while a generation runs: the work is already done server-side and closing would
      // abandon a result that is already saved but not yet shown.
      closeDisabled={generate.isPending}
      closeDisabledReason="Wait for the design to finish."
    >
      <div className="flex max-h-[78vh] flex-col">
      {postContext && <PostContextBand context={postContext} title="Designing for this post" />}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* ── Left: the form. Scrolls on its own; the footer stays put. ── */}
        <div className="flex min-h-0 w-full flex-col border-line md:w-[26.25rem] md:shrink-0 md:border-r">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            {/* First in the column, so it shows without scrolling from every way into this window. */}
            <p className="rounded-lg border border-line bg-page px-3 py-2 text-xs text-ink-label">
              Generate Design is still in beta.
            </p>

            {seed?.fromPost && (
              <Badge tone="brand">From post · text pre-filled</Badge>
            )}

            {/* 1 — Target */}
            <Field label="Target">
              <RadioRows
                name="design-target"
                value={target}
                onChange={(value) => {
                  setTarget(value as DesignTarget);
                  // The picked template belongs to the other format now, so it cannot survive.
                  setTemplate(null);
                  setTemplateMode('as-is');
                }}
                options={DESIGN_TARGETS.map((t) => ({
                  value: t.target,
                  label: t.label,
                  hint: t.format,
                }))}
              />
              <p className="mt-1.5 text-xs text-ink-muted">
                {DESIGN_TARGETS.find((t) => t.target === target)?.note}
              </p>
            </Field>

            {/* 2 — Template */}
            <Field label="Template">
              <RadioRows
                name="design-template-source"
                value={templateSource}
                onChange={(value) => {
                  const next = value as 'system' | 'custom';
                  setTemplateSource(next);
                  // Opening the right panel immediately: choosing the KIND of base and then having to
                  // find a second button to choose the base itself is a step for nothing.
                  setPane('template');
                }}
                options={[
                  {
                    value: 'system',
                    label: 'System template',
                    hint: archetype
                      ? (ARCHETYPE_CATALOG.find((a) => a.id === archetype)?.name ?? 'Chosen')
                      : 'One of five built-in layouts',
                    action: (
                      <Button size="sm" variant="secondary" onClick={() => { setTemplateSource('system'); setPane('template'); }}>
                        Choose
                      </Button>
                    ),
                  },
                  {
                    value: 'custom',
                    label: 'Custom template',
                    hint: template ? template.nickname : 'One you uploaded to this workspace',
                    action: (
                      <Button size="sm" variant="secondary" onClick={() => { setTemplateSource('custom'); setPane('template'); }}>
                        Choose
                      </Button>
                    ),
                  },
                ]}
              />

              {/*
                The live thumbnail, and the reason it exists: Size sits BELOW this field, so someone can
                approve a composition at 4:5, scroll down, switch to 9:16, and end up with a different
                design — with the picker already closed. This re-renders on every Size change, and for a
                system archetype it is the EXACT design Generate will return, not an impression of one.
              */}
              {hasBase && (
                <div className="mt-2 flex items-center gap-3 rounded-xl border border-line p-2.5">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-page">
                    {templateSource === 'custom' ? (
                      <TemplateChip template={template} />
                    ) : preview.url ? (
                      <img src={preview.url} alt="" className="max-h-full max-w-full bg-white object-contain" />
                    ) : (
                      <span className="text-[0.625rem] text-ink-faint">{preview.loading ? '…' : ''}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">
                      {templateSource === 'custom'
                        ? (template?.nickname ?? '')
                        : (ARCHETYPE_CATALOG.find((a) => a.id === archetype)?.name ?? '')}
                    </p>
                    {/* The size in effect, always visible next to the template it applies to. */}
                    <p className="truncate text-xs text-ink-muted">
                      {templateSource === 'custom' ? 'Custom template' : 'System template'} · {width} × {height}
                    </p>
                    {preview.notes.map((note) => (
                      <p key={note} className="truncate text-xs text-amber-700 dark:text-warning-dark">
                        {note}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Warn, never block — someone reusing a portrait template for a story is making a
                  deliberate choice, and the honest thing is to say the content may need nudging. */}
              {ratioWarning && <p className="mt-1.5 text-xs text-amber-700 dark:text-warning-dark">{ratioWarning}</p>}
            </Field>

            {/* 3 — Restyle, only once a template is chosen. */}
            {template && !template.staticOnly && (
              <Field label="Template treatment">
                <RadioRows
                  name="design-template-mode"
                  value={templateMode}
                  onChange={(value) => setTemplateMode(value as TemplateMode)}
                  options={TEMPLATE_MODES.map((m) => ({
                    value: m.value,
                    label: m.label,
                    hint:
                      pptxNoRestyle && m.value === 'restyle'
                        ? 'Not available for PowerPoint templates yet.'
                        : m.hint,
                  }))}
                />
                {pptxNoRestyle && (
                  <p className="mt-2 text-xs text-ink-muted">
                    Use as-is fills this template’s mapped regions and leaves the rest of the file
                    exactly as exported, so it keeps its own canvas size. Restyle rewrites every
                    colour in the file, which isn’t supported for PowerPoint yet.
                  </p>
                )}
              </Field>
            )}

            {/* 4 — Style prompt. Dimmed rather than hidden, so its existence stays discoverable. */}
            {/*
              Two selects where the free-text Design style field used to be, side by side in the row it
              occupied. The text field implied anything typed would be honoured while its real scope was
              palette and type — "add a drop shadow" was silently dropped, and a silent no-op costs more
              trust than a control that plainly cannot express the request.

              Two rather than one, because palette distribution and ground luminance are independent:
              "brand-forward on a dark ground" is a reasonable ask, and one blended list makes it
              unreachable.
            */}
            <div className="grid grid-cols-2 gap-2">
              <Field label="Color emphasis">
                <Select
                  aria-label="Color emphasis"
                  value={emphasis}
                  onChange={(e) => setEmphasis(e.target.value as ColorEmphasis)}
                >
                  {COLOR_EMPHASIS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-ink-muted">
                  {COLOR_EMPHASIS_OPTIONS.find((o) => o.value === emphasis)?.hint}
                </p>
              </Field>
              <Field label="Color mode">
                <Select
                  aria-label="Color mode"
                  value={colorMode}
                  onChange={(e) => setColorMode(e.target.value as ColorMode)}
                >
                  {COLOR_MODE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-ink-muted">
                  {COLOR_MODE_OPTIONS.find((o) => o.value === colorMode)?.hint}
                </p>
              </Field>
            </div>

            {/* 5 — The headline. Opened from a post, its first two lines are one press away; both
                fields stay editable afterwards. */}
            {postContext && (
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-ink-strong">Generate text from your post</p>
                <Button
                  size="sm"
                  variant="secondary"
                  className="min-h-11"
                  disabled={!postContext.text.trim()}
                  onClick={() => {
                    const lines = firstTwoLines(postContext.text);
                    setText(lines.text);
                    setSubtext(lines.subtext);
                  }}
                >
                  <Sparkles className="h-4 w-4" aria-hidden />
                  Generate text
                </Button>
                <p className="text-xs text-ink-muted">
                  {postContext.text.trim()
                    ? 'Fills Text and Second line from your post. Edit either before you generate.'
                    : 'Add text to your post to fill these from it.'}
                </p>
              </div>
            )}
            <Field
              label="Text"
              hint={seed?.fromPost ? 'Extracted from the post’s TEXT block — edit freely.' : undefined}
            >
              <Textarea
                rows={2}
                value={text}
                aria-label="Text"
                placeholder="The line to put on the design"
                onChange={(e) => setText(e.target.value)}
              />
            </Field>

            <Field label="Second line (optional)">
              <Input
                value={subtext}
                aria-label="Second line"
                placeholder="A supporting line, if the design has room"
                onChange={(e) => setSubtext(e.target.value)}
              />
            </Field>

            {/* 6 + 7 — preset and both edges on one row. */}
            <Field label="Size">
              <div className="grid grid-cols-[1.3fr,1fr,1fr] gap-2">
                <Select
                  aria-label="Aspect ratio"
                  value={presetId}
                  onChange={(e) => applyPreset(e.target.value)}
                >
                  {DESIGN_ASPECT_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </Select>
                <Input
                  type="number"
                  aria-label="Width in pixels"
                  value={width || ''}
                  onChange={(e) => editEdge('width', e.target.value)}
                />
                <Input
                  type="number"
                  aria-label="Height in pixels"
                  value={height || ''}
                  onChange={(e) => editEdge('height', e.target.value)}
                />
              </div>
              <p className="mt-1.5 text-xs text-ink-muted">
                {ratioLock
                  ? 'Editing one edge scales the other to keep this ratio. Choose Custom to unlink them.'
                  : 'Width and height are independent.'}
              </p>
              {!edgesValid && (
                <p className="mt-1 text-xs text-red-600 dark:text-error-dark">
                  Both edges must be between {DESIGN_MIN_EDGE} and {DESIGN_MAX_EDGE} pixels.
                </p>
              )}
            </Field>

            {/* 8 — Image source. */}
            <Field label="Image">
              {!templateTakesImage ? (
                <p className="rounded-xl bg-page px-3 py-2.5 text-sm text-ink-label">
                  {template?.staticOnly ? (
                    <>
                      Image source is unavailable — this template is a{' '}
                      <strong className="font-medium">static background</strong>, so the background is
                      the design&rsquo;s image.
                    </>
                  ) : (
                    <>
                      Image source is unavailable — this template has no region mapped to{' '}
                      <strong className="font-medium">Image</strong>.
                    </>
                  )}
                </p>
              ) : (
                <>
                  <RadioRows
                    name="design-image"
                    value={backgroundSource}
                    onChange={(value) => {
                      const next = value as DesignBackgroundSource;
                      setBackgroundSource(next);
                      if (next === 'library') setPane('image');
                      else {
                        // Cleared, so a design cannot ship the last-chosen photo after someone switched
                        // away from the library.
                        setImageAssetId(null);
                        setImageThumbUrl(null);
                        setImageName(null);
                      }
                    }}
                    options={DESIGN_BACKGROUND_SOURCES.map((s) => ({
                      value: s.value,
                      label: s.label,
                      hint: s.value === 'library' ? (imageName ?? s.hint) : s.hint,
                      // The picture itself, so the choice is checkable without reopening the picker.
                      visual:
                        s.value === 'library' && imageAssetId ? (
                          <ChosenImageThumb assetId={imageAssetId} thumbUrl={imageThumbUrl} />
                        ) : undefined,
                      // Only the two GENERATING options carry settings; RadioRows reveals them for
                      // whichever of the two is selected.
                      detail: s.costsQuota ? imageOptionsPanel : undefined,
                      action:
                        s.value === 'library' ? (
                          <Button size="sm" variant="secondary" onClick={() => setPane('image')}>
                            Choose
                          </Button>
                        ) : undefined,
                    }))}
                  />

                </>
              )}
            </Field>
          </div>

          {/* What the buttons below will do, or why they can't yet. Kept with the form it describes;
              the button bar itself runs across the whole bottom of the window. */}
          <div className="shrink-0 space-y-2 border-t border-line bg-card px-4 py-3">
            <p className="text-xs text-ink-muted">Mode: {DESIGN_MODE_LABEL[mode]}</p>
            {!canGenerate && !generate.isPending && blockedBecause && (
              <p className="text-xs text-ink-muted">{blockedBecause}</p>
            )}
            {progress && (
              // Shown WHILE the button spins, because a spinner alone cannot distinguish a model that
              // is working from one that has hung. The seconds in the server's sentence keep moving,
              // which is the difference.
              <p className="text-sm text-ink-label" role="status" aria-live="polite">
                {progress}
              </p>
            )}
            {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
          </div>
        </div>

        {/* ── Right: preview, or one of the two pickers. ── */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {pane === 'template' ? (
            templateSource === 'system' ? (
              <SystemTemplatePanel
                selected={archetype}
                width={width}
                height={height}
                emphasis={emphasis}
                colorMode={colorMode}
                hasImage={backgroundSource !== 'flat'}
                variantSeed={variantSeed}
                onPick={(id: ArchetypeId) => {
                  setArchetype(id);
                  // Size is deliberately untouched: an archetype is a parametric function with no
                  // native size, so there is nothing to adopt.
                  setPane('preview');
                }}
                onBack={() => setPane('preview')}
              />
            ) : (
              <TemplatePicker
                templates={available}
                loading={templates.isLoading}
                selectedId={template?.id ?? null}
                target={target}
                onPick={chooseTemplate}
                onBack={() => setPane('preview')}
              />
            )
          ) : pane === 'image' ? (
            <div
              className="flex min-h-0 flex-1 flex-col"
              // The panel is a fixed-height card that reads its height from this variable. Setting it
              // here is what makes it fill the pane instead of standing at its default 32rem inside a
              // taller dialog. Set on the wrapper, not the panel, so its own component is untouched.
              style={{ ['--composer-pane-h' as string]: '100%' }}
            >
              {/* A slim bar, NOT a titled header: the panel renders its own "Media Library" heading,
                  and a second title above it would read as two nested panels. This carries only what
                  the panel has no way to know — that one image is the limit, and how to get back. */}
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-2">
                <Badge tone="neutral">Single select — one image per design</Badge>
                <Button size="sm" variant="ghost" onClick={() => setPane('preview')}>
                  Back to preview
                </Button>
              </div>
              {/* The composer's own picker, reused rather than reimplemented. `remaining: 1` is what
                  makes it single-select: one design takes one image. */}
              <div className="min-h-0 flex-1">
                <MediaLibraryPanel
                  remaining={1}
                  // A design background cannot be a video, and the engine drops anything that is not
                  // PNG or JPEG — so the picker must not offer one. Same allowlist the server applies.
                  accept={isGenerationReferenceMime}
                  acceptLabel="JPG or PNG"
                  onClose={() => setPane('preview')}
                  onInsert={(assets) => {
                    const first = assets[0];
                    if (first) {
                      setImageAssetId(first.id);
                      setImageThumbUrl(first.thumbUrl ?? null);
                      setImageName(nameFromCache(queryClient, first.id));
                      setBackgroundSource('library');
                    }
                    setPane('preview');
                  }}
                />
              </div>
            </div>
          ) : (
            <PreviewPane
              result={result}
              pending={generate.isPending}
              mode={mode}
              warning={ratioWarning}
              toPost={onSaveToPost != null}
              onLeave={onClose}
              onRefined={(design) => {
                setResult(design);
                // The refinement is its own asset, so the library gains a row rather than changing one.
                void queryClient.invalidateQueries({ queryKey: ['media'] });
              }}
            />
          )}

          {libraryOpen && (
            // Over the preview pane, which is the surface this modal owns — same arrangement as
            // Generate media, so picking a reference feels identical in both places.
            <MediaLibraryOverlay
              selected={imageRefs}
              max={maxImageRefs}
              onChange={setImageRefs}
              onClose={() => setLibraryOpen(false)}
              onUpload={() => setUploadSignal((n) => n + 1)}
            />
          )}
        </div>
      </div>

      {/*
        Across the whole bottom, right-aligned. Once there is a design, Generate steps back to a
        secondary "Try another version" and the way out leads: "Download and close" (primary, last)
        hands over the editable file and adds nothing to the post; "Save and return to post", from
        the post editor only, puts a PNG copy in the post, since a post cannot carry an .svg or .pptx.
      */}
      <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-line bg-card px-4 py-3">
        <Button variant="secondary" disabled={generate.isPending || leaving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={result ? 'secondary' : 'primary'}
          loading={generate.isPending}
          disabled={!canGenerate || leaving}
          onClick={() => {
            // A fresh seed on every press. With the layout now chosen by the user, re-rolling
            // this is the entire variety mechanism — it varies colour, type and proportion, and
            // deliberately not the structure, which is the archetype they picked.
            if (result) setVariantSeed(Math.floor(Math.random() * 2_147_483_647));
            generate.mutate();
          }}
        >
          {result ? 'Try another version' : 'Generate'}
        </Button>
        {result && onSaveToPost && (
          <Button
            variant="secondary"
            loading={savePng.isPending}
            disabled={generate.isPending || leaving}
            onClick={() => savePng.mutate(result.assetId)}
          >
            Save and return to post
          </Button>
        )}
        {result && (
          <Button
            loading={download.isPending}
            disabled={generate.isPending || leaving}
            onClick={() => download.mutate(result)}
          >
            Download and close
          </Button>
        )}
      </div>
      </div>
    </Modal>
  );
}

/** "Proxima Nova", "Proxima Nova and Gotham", "A, B and C". */
function familyList(families: string[]): string {
  if (families.length <= 1) return families[0] ?? '';
  return `${families.slice(0, -1).join(', ')} and ${families[families.length - 1]}`;
}

/**
 * The chosen image's filename, read from the cache the picker just populated.
 *
 * The picker hands back only `{id, blobUrl, mimeType}` — no name — and refetching one asset to render
 * a subtitle would be a request for a label. The library list that the picker itself rendered from is
 * already in the cache, so the name is there for free.
 */
function nameFromCache(client: QueryClient, assetId: string): string {
  return findCachedAsset(client, assetId)?.name?.trim() || 'Image selected';
}

/** A labelled block. Keeps the nine fields visually identical without a wrapper per field. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      {/* A div, not a <label>: several children are designed Selects, whose hidden native control
          precedes the trigger — a label without htmlFor would bind to the wrong one. */}
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-medium text-ink-strong">{label}</span>
        {hint && <span className="text-xs text-ink-muted">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

/** The current template's thumbnail, or the sparkle that stands for "no template". */
function TemplateChip({ template }: { template: BrandWorkspaceTemplate | null }) {
  const src = useProxyImage(
    template?.kind === BrandTemplateKind.FIGMA_SVG
      ? template.fileUrl
      : (template?.previewUrl ?? null),
  );
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-page">
      {src ? (
        <img src={src} alt="" className="max-h-full max-w-full bg-white object-contain" />
      ) : (
        <Sparkles className="h-4 w-4 text-action" aria-hidden />
      )}
    </div>
  );
}

/** Single-select grid of the workspace's templates for the active target, plus a "No template" tile. */
function TemplatePicker({
  templates,
  loading,
  selectedId,
  target,
  onPick,
  onBack,
}: {
  templates: BrandWorkspaceTemplate[];
  loading: boolean;
  selectedId: string | null;
  target: DesignTarget;
  onPick: (template: BrandWorkspaceTemplate | null) => void;
  onBack: () => void;
}) {
  const [pending, setPending] = useState<BrandWorkspaceTemplate | null | undefined>(undefined);
  const staged = pending === undefined ? templates.find((t) => t.id === selectedId) ?? null : pending;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="text-sm font-medium text-ink">
          {staged ? `1 of ${templates.length} templates` : `No template selected · ${templates.length} available`}
        </span>
        <Button size="sm" variant="ghost" onClick={onBack}>
          Back to preview
        </Button>
      </div>

      {/* content-sized rows: a grid inside a height-capped flex column compresses its rows to fit
          instead of scrolling unless they are told to size to their content. */}
      <div
        className="min-h-0 flex-1 overflow-y-auto p-4"
        style={{ gridAutoRows: 'max-content', alignContent: 'start' }}
      >
        {loading ? (
          <p className="text-sm text-ink-muted">Loading templates…</p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {templates.map((t) => (
              <li key={t.id}>
                <TemplateTile
                  template={t}
                  selected={staged?.id === t.id}
                  onSelect={() => setPending(t)}
                />
              </li>
            ))}
            {templates.length === 0 && (
              <li className="col-span-full">
                <p className="text-sm text-ink-muted">
                  No {target === 'figma' ? 'Figma' : 'Canva'} templates in this workspace yet. Upload
                  one under Edit Brand Workspace → Design templates, or generate without a template.
                </p>
              </li>
            )}
          </ul>
        )}
      </div>

      <div className="flex shrink-0 justify-end border-t border-line bg-card p-3">
        <Button onClick={() => onPick(staged ?? null)}>Use template</Button>
      </div>
    </div>
  );
}

function TemplateTile({
  template,
  selected,
  onSelect,
}: {
  template: BrandWorkspaceTemplate;
  selected: boolean;
  onSelect: () => void;
}) {
  const src = useProxyImage(
    template.kind === BrandTemplateKind.FIGMA_SVG ? template.fileUrl : template.previewUrl,
  );
  const mapped = template.roleMap && Object.keys(template.roleMap).length > 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`focus-ring w-full overflow-hidden rounded-xl border-2 text-left ${
        selected ? 'border-action' : 'border-line'
      }`}
    >
      <div className="flex aspect-[4/3] items-center justify-center bg-page">
        {src ? (
          <img src={src} alt="" className="max-h-full max-w-full bg-white object-contain" />
        ) : (
          <span className="px-2 text-center text-xs text-ink-faint">No preview</span>
        )}
      </div>
      <div className="space-y-1 p-2">
        <p className="truncate text-sm font-medium text-ink">{template.nickname}</p>
        {/* The Phase 1 flags, shown BEFORE the choice — an unmapped template cannot take text, and
            finding that out after generating wastes the round trip. */}
        {template.staticOnly ? (
          <Badge tone="neutral">Static background — overlay mode</Badge>
        ) : !mapped ? (
          <Badge tone="amber">Roles not mapped yet</Badge>
        ) : (
          <span className="text-xs text-ink-muted">
            {template.widthPx && template.heightPx
              ? `${template.widthPx}×${template.heightPx}`
              : 'Native size unknown'}
          </span>
        )}
      </div>
    </button>
  );
}

/** Empty, loading and result — the three phases of the preview pane. */
function PreviewPane({
  result,
  pending,
  mode,
  warning,
  toPost,
  onLeave,
  onRefined,
}: {
  result: GeneratedDesign | null;
  pending: boolean;
  mode: DesignMode;
  warning: string | null;
  /** Opened from the post editor, where "Save and return to post" adds a PNG copy to the post. */
  toPost: boolean;
  /** Closes the dialog before navigating — a modal left open over the library traps focus. */
  onLeave: () => void;
  /** A refinement produced a new design; the panel shows it and the previous one stays in the library. */
  onRefined: (design: GeneratedDesign) => void;
}) {
  const navigate = useNavigate();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const src = useProxyImage(result?.fileUrl ?? null);
  const isVector = result?.mimeType === 'image/svg+xml';
  const previewState = designPreviewState({ isVector, src });
  const ext = isVector ? '.svg' : '.pptx';
  const tool = result?.target === 'figma' ? 'Figma' : 'Canva';
  const missing = result?.missingFonts ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {warning && !pending && (
        <div className="shrink-0 p-4 pb-0">
          <AlertBanner tone="warning">{warning}</AlertBanner>
        </div>
      )}
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-6">
        {pending ? (
          <p className="text-sm text-ink-muted">{DESIGN_MODE_STATUS[mode]}</p>
        ) : !result ? (
          <p className="max-w-sm text-center text-sm text-ink-muted">
            Nothing generated yet. Choose a template or generate from your brand assets, add the line
            you want on it, and press Generate.
          </p>
        ) : (
          <div className="w-full space-y-3">
            <div className="flex items-center justify-center rounded-xl border border-line bg-card p-2">
              {/*
                Three states, not two. Fetching the image is a SEPARATE async step from generating
                it — the file is bearer-guarded, so `src` is null for a moment after the design
                arrives — and folding that moment into the else branch made an .svg flash "PowerPoint
                files can't be previewed here" on its way to rendering. The format decides which
                message is possible; the fetch only decides whether we have the picture yet.
              */}
              {previewState === 'unsupported-format' ? (
                // A .pptx cannot be shown here: nothing in this modal can render one, and a fake
                // preview would be worse than saying so.
                <p className="px-4 py-10 text-center text-sm text-ink-muted">
                  PowerPoint files can’t be previewed here. Download it and open it in Canva.
                </p>
              ) : previewState === 'ready' ? (
                // `?? undefined` because the state machine, not the type system, is what guarantees
                // src is present in the 'ready' branch — and an empty string here would render a
                // broken image rather than nothing.
                <img
                  src={src ?? undefined}
                  alt={result.filename}
                  className="max-h-[46vh] w-auto bg-white object-contain"
                />
              ) : (
                <p className="px-4 py-10 text-center text-sm text-ink-muted">Loading preview…</p>
              )}
            </div>
            <p className="break-all text-sm font-medium text-ink">{result.filename}</p>
            <p className="text-xs text-ink-label">
              Saved to your Media Library.{' '}
              {toPost && (
                <>
                  <strong className="font-medium">Save and return to post</strong> adds a PNG copy to
                  your post.{' '}
                </>
              )}
              Open the {ext} in {tool} to keep editing.
            </p>
            {missing.length > 0 && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-warning-dark dark:bg-warning-dark-tint dark:text-warning-dark">
                {familyList(missing)} {missing.length === 1 ? 'isn’t' : 'aren’t'} installed here, so
                the preview{toPost ? ' and the PNG copy use' : ' uses'} a similar font. Your {ext}{' '}
                keeps {familyList(missing)}.
              </p>
            )}
            {result.notes.length > 0 && (
              <ul className="space-y-1 rounded-xl bg-amber-50 dark:bg-warning-dark-tint p-3 text-xs text-amber-900 dark:text-warning-dark">
                {result.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )}

            {/* Above Download, so the next step on a design that is nearly right is the first thing
                under it. */}
            <RefineBox assetId={result.assetId} onRefined={onRefined} />

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  void downloadAssetByUrl(result.fileUrl, result.filename).catch((err: Error) =>
                    setDownloadError(err.message),
                  )
                }
              >
                Download
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onLeave();
                  navigate(`/media?asset=${encodeURIComponent(result.assetId)}`);
                }}
              >
                View in Media Library
              </Button>
            </div>
            {downloadError && <p className="text-sm text-red-600 dark:text-error-dark">{downloadError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Ask for a change in plain language and get a new version.
 *
 * A NEW version, always — the design being edited took a minute to make and is never overwritten, so
 * three refinements leave four designs in the library and any of them can be gone back to.
 *
 * The server decides what a sentence means and what it costs: a text, size or colour ask never reaches
 * an image model, and one that does returns a job to watch exactly like Generate. So this component
 * handles both shapes and otherwise stays out of the way.
 */
function RefineBox({
  assetId,
  onRefined,
}: {
  assetId: string;
  onRefined: (design: GeneratedDesign) => void;
}) {
  const [ask, setAsk] = useState('');
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refine = useMutation({
    mutationFn: async () => {
      setError(null);
      setProgress(null);
      const response = await api.refineDesign({ assetId, request: ask.trim() });
      if (response.status === 'ready') return response.design;
      setProgress('Regenerating the image…');
      return awaitDesignJob(response.jobId, setProgress);
    },
    onSuccess: (design) => {
      setProgress(null);
      // Cleared on success only: a failed ask stays in the box so it can be reworded rather than retyped.
      setAsk('');
      onRefined(design);
    },
    onError: (err) => {
      setProgress(null);
      setError(err instanceof Error ? err.message : 'That change could not be applied.');
    },
  });

  const canSend = ask.trim().length > 1 && !refine.isPending;

  return (
    <div className="space-y-2 rounded-xl border border-line p-3">
      <Field
        label="Refine this design"
        hint="Describe one change. Anything you don’t mention stays exactly as it is."
      >
        <Textarea
          rows={2}
          value={ask}
          disabled={refine.isPending}
          aria-label="Describe a change"
          placeholder="e.g. make the logo bigger, make the accent rule orange, or remove the subhead"
          onChange={(e) => setAsk(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line — the convention every chat box uses, and this
            // is a one-line ask far more often than a paragraph.
            if (e.key === 'Enter' && !e.shiftKey && canSend) {
              e.preventDefault();
              refine.mutate();
            }
          }}
        />
      </Field>
      {progress && (
        <p className="text-sm text-ink-label" role="status" aria-live="polite">
          {progress}
        </p>
      )}
      {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
      <div className="flex justify-end">
        <Button size="sm" loading={refine.isPending} disabled={!canSend} onClick={() => refine.mutate()}>
          Send
        </Button>
      </div>
    </div>
  );
}

/**
 * The chosen Media Library image, shown on its own radio row.
 *
 * Bearer-guarded like every other asset, so it goes through the proxy hook rather than a plain `src` —
 * a direct URL renders as a broken image. Small and fixed-size: this is a confirmation that the right
 * picture is attached, not a preview of the design, which the pane beside it already shows.
 */
function ChosenImageThumb({ assetId, thumbUrl }: { assetId: string; thumbUrl: string | null }) {
  // The small copy when the asset has one. A 36px confirmation chip has no business downloading a
  // camera original, which is what this did when it was written.
  const src = useProxyImage(thumbUrl ?? `/api/assets/${assetId}/image`);
  return (
    <span className="block h-9 w-9 overflow-hidden rounded-md border border-line bg-page">
      {src && <img src={src} alt="" className="h-full w-full bg-white object-cover" />}
    </span>
  );
}
